import {
  calcMonthlyRevenues,
  calcOneTimeRevenuesPerMonth,
  calcStripRevenuePerMonth,
  calcTotalEmrCost,
  getSelectedYearMonthsArray,
  getStartAndEndGlucometer,
} from '@/constants/site-stats'
import moment from 'moment-timezone'
import { ONE_DAY } from '@/utils/time'
import { SiteStats, SiteStatsAttributes } from '@/jsonapi/SiteStats'
import site from '@/store/modules/site'
import { deepGet } from '@orbit/utils'

export const MIN_TIMESTAMP = 1577836800000 // 2020-01-01

export const MonthlyOpexCostTypes = {
  support: 'support',
  sw_dev: 'sw_dev',
  other_monthly: 'other_monthly',
  aws_fee: 'aws_fee',
  mongodb_fee: 'mongodb_fee',
}

export const MonthlyCapexCostTypes = {
  rpi: 'rpi',
  rock_pi: 'rock_pi',
  other_capex: 'other_capex',
  other: 'other', // some added previously
}

export type SiteCashFlowStats = {
  siteId: string
  siteName: string
  org: string
  monthlyRevenues: number[]
  stripRevenuePerMonth: number[]
  oneTimeRevenues: number[]
  gatewaySalesPerMonth: number[]
  installationFeesPerMonth: number[]
}

export type MonthlyCost = {
  id: string
  type: string
  year: number
  month: number
  cost: number
}

export class CashFlowCalculator {
  siteStats: SiteStatsAttributes[]
  siteCashFlowStats: SiteCashFlowStats[]
  monthlyCostsData: MonthlyCost[]
  startTimestamp: number
  endTimestamp: number
  skipEmptyMonths: boolean = false

  months: string[]
  numberOfMonths: number

  constructor(
    siteStats: SiteStatsAttributes[],
    monthlyCostsData: MonthlyCost[],
    startTimestamp: number,
    endTimestamp: number
  ) {
    this.siteStats = siteStats
    this.siteCashFlowStats = this.mapSiteStatsToCashFlowStats(siteStats)
    this.monthlyCostsData = monthlyCostsData
    this.startTimestamp = startTimestamp
    this.endTimestamp = endTimestamp

    this.init()
  }

  // public methods

  getEmrCostsPerMonth(): object {
    // map of monthStr to number (all homes combined)
    const EMR_COST_PER_MONTH = 45 // using a variable now

    let months = getSelectedYearMonthsArray(this.startTimestamp, this.endTimestamp)
    let output = {}
    for (let yearMonth of months) {
      let monthTs = moment(yearMonth + '-01', 'YYYYMM-DD').valueOf()
      let startOfMonthTimestamp = monthTs
      let roughEndOfMonthTimestamp = monthTs + ONE_DAY * 30

      let monthStr = this.getMonthStr(startOfMonthTimestamp)

      let num = 0
      for (let thisSiteStats of this.siteStats) {
        let tuple = getStartAndEndGlucometer(thisSiteStats.siteUsageStats)
        let start = tuple[0]
        let end = tuple[1]

        if (end != 0 && end < monthTs) {
          continue // don't add cost if the program already ended
        }
        if (start > roughEndOfMonthTimestamp) {
          continue // don't add cost if the program hasn't started yet
        }
        if (
          !thisSiteStats.siteUsageStats.revenuesAndCosts ||
          !thisSiteStats.siteUsageStats.revenuesAndCosts[yearMonth]
        ) {
          continue
        }
        let emrCostThisMonth = thisSiteStats.siteUsageStats.revenuesAndCosts[yearMonth].monthlyEmrCost
        num += emrCostThisMonth
      }
      output[monthStr] = num
    }
    return output
  }
  public getMonthStr(timestamp: number): string {
    if (this.numberOfMonths > 11) {
      return moment.utc(timestamp).format('MMM YYYY')
    } else {
      return moment.utc(timestamp).format('MMM')
    }
  }
  getMonthStrFromYearAndMonth(year: number, month: number): string {
    let m = moment.utc({ year: year, month: month - 1, day: 1 })
    return this.getMonthStr(m.unix() * 1000)
  }

  getCustomCostsByMonth(includeOpex = true, includeCapex = true): object {
    let obj = {}
    for (let cost of this.monthlyCostsData) {
      if (cost.type in MonthlyOpexCostTypes) {
        if (!includeOpex || cost.cost === 0) {
          continue
        }
      } else if (cost.type in MonthlyCapexCostTypes) {
        if (!includeCapex || cost.cost === 0) {
          continue
        }
      } else {
        throw new Error('Unknown Expenditure type: ' + cost.type)
      }
      // @ts-ignore
      let monthStr = this.getMonthStrFromYearAndMonth(cost.year, cost.month)
      if (!(monthStr in obj)) {
        obj[monthStr] = {}
      }
      // @ts-ignore
      obj[monthStr][cost.type] = cost.cost
    }
    return obj
  }

  getOneTimeRevenuesByMonth(): object {
    let obj = {}
    for (let cost of this.monthlyCostsData) {
      // @ts-ignore
      let monthStr = this.getMonthStrFromYearAndMonth(cost.year, cost.month)
      if (!(monthStr in obj)) {
        obj[monthStr] = {}
      }
      // @ts-ignore
      obj[monthStr][cost.type] = cost.cost
    }
    return obj
  }

  getTotalRevenuesPerMonth(includeMonthly = true, includeStrip = true, includeOneTime = true): object {
    // map of monthStr to number (all homes combined)
    if (!this.siteStats || this.siteStats.length === 0) {
      return []
    }
    let output = {}
    // initialize
    for (let monthStr of this.months) {
      output[monthStr] = 0
    }
    this.siteCashFlowStats.forEach((site, index) => {
      if (includeMonthly) {
        site.monthlyRevenues.forEach((rev, monthIndex) => {
          let monthStr = this.months[monthIndex]
          output[monthStr] += rev
        })
      }
      if (includeStrip) {
        site.stripRevenuePerMonth.forEach((rev, monthIndex) => {
          let monthStr = this.months[monthIndex]
          output[monthStr] += rev
        })
      }
      if (includeOneTime) {
        site.oneTimeRevenues.forEach((rev, monthIndex) => {
          let monthStr = this.months[monthIndex]
          output[monthStr] += rev
        })
      }
    })
    // todo, one time revenues
    return output
  }
  getTotalOneTimeRevenues(includeSales = true, includeInstallationFees = true): number {
    let r = this.siteCashFlowStats.reduce(
      (a1, site) =>
        a1 +
        // eslint-disable-next-line prettier/prettier
        (includeSales ? site.gatewaySalesPerMonth.reduce((a2, b2) => a2 + b2, 0) : 0) +
        (includeInstallationFees ? site.installationFeesPerMonth.reduce((a2, b2) => a2 + b2, 0) : 0),
      0
    )
    return r
  }
  getTotalRevenue(includeMonthly = true, includeStrip = true, includeOneTime = true): number {
    let r = this.siteCashFlowStats.reduce(
      (a1, site) =>
        a1 +
        // eslint-disable-next-line prettier/prettier
        (includeOneTime ? site.oneTimeRevenues.reduce((a2, b2) => a2 + b2, 0) : 0) +
        (includeMonthly ? site.monthlyRevenues.reduce((a2, b2) => a2 + b2, 0) : 0) +
        (includeStrip ? site.stripRevenuePerMonth.reduce((a2, b2) => a2 + b2, 0) : 0),
      0
    )
    return r
  }

  getTotalExpensesPerMonth(includeEmr = true, includeOpex = true, includeCapex = true): object {
    if (!this.siteStats || this.siteStats.length === 0) {
      return []
    }
    let output = {}

    let emrCostsPerMonth = includeEmr ? this.getEmrCostsPerMonth() : {} // map of monthStr to number (all homes combined)
    let opexCostsPerMonth = includeOpex ? this.getCustomCostsByMonth(true, false) : 0
    let capexCostsPerMonth = includeCapex ? this.getCustomCostsByMonth(false, true) : 0

    for (let monthStr of this.months) {
      // console.log('checking month' + monthStr)
      let emrCost = emrCostsPerMonth[monthStr] || 0
      let opexCosts = Object.values(opexCostsPerMonth[monthStr] || {}).reduce(
        (total: number, cost: number) => total + cost,
        0
      )
      let capexCosts = Object.values(capexCostsPerMonth[monthStr] || {}).reduce(
        (total: number, cost: number) => total + cost,
        0
      )

      let val = emrCost + opexCosts + capexCosts

      if (val > 0 || !this.skipEmptyMonths) {
        output[monthStr] = val
        // @ts-ignore
        if (opexCosts + capexCosts > 0) {
          // console.log(`Adding ${emrCost} EMR + ${opexCosts} custom opex + ${capexCosts} capex to ${monthStr}`)
        }
      }
    }
    // console.log('total expenses per month: ', includeEmr, includeOpex, includeCapex,
    //   'this.months:', this.months,
    //   'inputs:', emrCostsPerMonth, customCostsPerMonth,
    //   'output:', output)
    return output
  }

  getTotalExpenses(includeEmr = true, includeOpex = true, includeCapex = true): number {
    return Object.values(this.getTotalExpensesPerMonth(includeEmr, includeOpex, includeCapex)).reduce(
      (a: number, b: number) => a + b,
      0
    ) as number
  }

  // private methods
  init() {
    console.log(
      'CashFlowCalculator init(): ',
      this.siteCashFlowStats,
      this.monthlyCostsData,
      this.startTimestamp,
      this.endTimestamp
    )
    this.months = this.calculateMonths()
  }

  mapSiteStatsToCashFlowStats(siteStats: SiteStatsAttributes[]): SiteCashFlowStats[] {
    return siteStats
      ?.filter(site => !site.sandbox)
      ?.sort((a, b) => (a.org + a.siteName).localeCompare(b.org + b.siteName))
      ?.map(siteStats => ({
        siteId: siteStats.siteUsageStats.siteId,
        siteName: siteStats.siteName,
        org: siteStats.org,
        monthlyRevenues: calcMonthlyRevenues(siteStats.siteUsageStats),
        stripRevenuePerMonth: calcStripRevenuePerMonth(siteStats.siteUsageStats),
        oneTimeRevenues: calcOneTimeRevenuesPerMonth(siteStats.siteUsageStats),
        gatewaySalesPerMonth: calcOneTimeRevenuesPerMonth(siteStats.siteUsageStats, true, false),
        installationFeesPerMonth: calcOneTimeRevenuesPerMonth(siteStats.siteUsageStats, false, true),
        // monthlyCosts: calcMonthlyCost(siteStats.siteUsageStats, true),
      }))
  }

  private calculateMonths(): string[] {
    this.numberOfMonths = getSelectedYearMonthsArray(this.startTimestamp, this.endTimestamp).length
    if (!this.siteStats || this.siteStats.length === 0) {
      return []
    }

    let output = []

    let ts = this.startTimestamp
    if (ts == null) return
    ts = moment(ts)
      .startOf('month')
      .valueOf()
    while (ts < this.endTimestamp) {
      output.push(this.getMonthStr(ts))
      ts =
        moment(ts)
          .startOf('month')
          .add(1, 'month')
          .valueOf() + ONE_DAY
    }
    return output
  }
}
